import type { Expense, Revenue, FinancialEvent } from "../../types/startup.types";
import { formatCalendarDate, parseCalendarDate } from "../date";

export type RevisionItemKind = "expense" | "revenue" | "asset";

export interface SegmentChainMember {
  id: string;
  kind: RevisionItemKind;
  label: string;
  startDate: Date;
  endDate?: Date | null;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
  amountLabel: string;
  original: Expense | Revenue | FinancialEvent;
}

export interface SegmentChain {
  groupId: string;
  kind: RevisionItemKind;
  label: string;
  segments: SegmentChainMember[];
}

function segmentStartDate(
  item: Expense | Revenue | FinancialEvent,
  kind: RevisionItemKind,
): Date {
  if (kind === "asset") {
    const event = item as FinancialEvent;
    return parseCalendarDate(event.date) ?? new Date(0);
  }
  const dated = item as Expense | Revenue;
  return (
    parseCalendarDate(dated.startDate) ??
    parseCalendarDate((dated as Revenue).date) ??
    new Date(0)
  );
}

function segmentEndDate(
  item: Expense | Revenue | FinancialEvent,
): Date | undefined {
  const end = (item as Expense | Revenue | FinancialEvent).endDate;
  return end ? (parseCalendarDate(end) ?? undefined) : undefined;
}

function formatAmountLabel(
  item: Expense | Revenue | FinancialEvent,
  kind: RevisionItemKind,
): string {
  if (kind === "asset") {
    const event = item as FinancialEvent;
    const rate = event.monthlyAppreciationRate;
    const ratePct =
      rate != null ? `${(rate * 100).toFixed(2)}%/mo` : "flat";
    return `$${Number(event.amount ?? 0).toLocaleString()} @ ${ratePct}`;
  }
  if (kind === "expense") {
    const expense = item as Expense;
    if (expense.isOneTime) {
      return `$${Number(expense.oneTimeAmount ?? 0).toLocaleString()} one-time`;
    }
    return `$${Number(expense.monthlyAmount ?? expense.amount ?? 0).toLocaleString()}/mo`;
  }
  const revenue = item as Revenue;
  return `$${Number(revenue.monthlyAmount ?? revenue.amount ?? 0).toLocaleString()}/mo`;
}

function toMember(
  item: Expense | Revenue | FinancialEvent,
  kind: RevisionItemKind,
): SegmentChainMember {
  const label =
    kind === "asset"
      ? (item as FinancialEvent).description || "(asset)"
      : (item as Expense | Revenue).name;

  return {
    id: item.id,
    kind,
    label,
    startDate: segmentStartDate(item, kind),
    endDate: segmentEndDate(item),
    segmentKind: item.segmentKind,
    revisesId: item.revisesId,
    revisionNote: item.revisionNote,
    amountLabel: formatAmountLabel(item, kind),
    original: item,
  };
}

export function getSegmentGroupId(item: {
  id: string;
  lineItemGroupId?: string;
}): string {
  return item.lineItemGroupId ?? item.id;
}

export function buildSegmentChains(input: {
  expenses: Expense[];
  revenues: Revenue[];
  events: FinancialEvent[];
}): SegmentChain[] {
  const chains: SegmentChain[] = [];

  const addChains = <T extends Expense | Revenue | FinancialEvent>(
    items: T[],
    kind: RevisionItemKind,
    filter: (item: T) => boolean,
  ) => {
    const eligible = items.filter(filter);
    const byGroup = new Map<string, T[]>();

    for (const item of eligible) {
      const key = getSegmentGroupId(item);
      const list = byGroup.get(key) ?? [];
      list.push(item);
      byGroup.set(key, list);
    }

    for (const [groupId, groupItems] of byGroup) {
      if (groupItems.length < 2) continue;
      const segments = groupItems
        .map((item) => toMember(item, kind))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

      chains.push({
        groupId,
        kind,
        label: segments[0]?.label ?? groupId,
        segments,
      });
    }
  };

  addChains(
    input.expenses,
    "expense",
    (e) => !e.isOneTime && e.frequency !== "one-time",
  );
  addChains(
    input.revenues,
    "revenue",
    (r) => r.isRecurring !== false && r.frequency !== "one-time" && !r.isCustomerDriven,
  );
  addChains(input.events, "asset", (e) => e.type === "asset");

  return chains.sort((a, b) => a.label.localeCompare(b.label));
}

export function findSegmentChain(
  chains: SegmentChain[],
  groupId: string,
): SegmentChain | undefined {
  return chains.find((chain) => chain.groupId === groupId);
}

export function formatSegmentDateRange(
  startDate: Date,
  endDate?: Date | null,
): string {
  const start = formatCalendarDate(startDate);
  const end = endDate ? formatCalendarDate(endDate) : "ongoing";
  return `${start} → ${end}`;
}

export interface RevisionTimelineMarker {
  id: string;
  date: Date;
  progress: number;
  kind: RevisionItemKind;
  name: string;
  label: string;
  color: string;
}

const REVISION_MARKER_COLOR = "#38bdf8";

export function buildRevisionTimelineMarkers(
  input: {
    expenses: Array<{ id: string; name: string; startDate: Date; segmentKind?: string; revisionNote?: string; monthlyAmount?: number; amount?: number }>;
    revenues: Array<{ id: string; name: string; startDate: Date; segmentKind?: string; revisionNote?: string; monthlyAmount?: number; amount?: number }>;
    events: Array<{ id: string; description?: string; date: Date; type: string; segmentKind?: string; revisionNote?: string; amount?: number; monthlyAppreciationRate?: number }>;
  },
  visibleStart: Date,
  visibleEnd: Date,
): RevisionTimelineMarker[] {
  const rangeMs = visibleEnd.getTime() - visibleStart.getTime();
  if (rangeMs <= 0) return [];

  const markers: RevisionTimelineMarker[] = [];

  const pushMarker = (
    id: string,
    date: Date,
    kind: RevisionItemKind,
    name: string,
    detail: string,
  ) => {
    const progress =
      (date.getTime() - visibleStart.getTime()) / rangeMs;
    if (progress < 0 || progress > 1) return;

    markers.push({
      id,
      date,
      progress,
      kind,
      name,
      label: detail,
      color: REVISION_MARKER_COLOR,
    });
  };

  for (const expense of input.expenses) {
    if (expense.segmentKind !== "revision") continue;
    const amount = expense.monthlyAmount ?? expense.amount ?? 0;
    pushMarker(
      `revision-expense-${expense.id}`,
      expense.startDate,
      "expense",
      expense.name,
      `${expense.name} revised → $${Number(amount).toLocaleString()}/mo${expense.revisionNote ? ` — ${expense.revisionNote}` : ""}`,
    );
  }

  for (const revenue of input.revenues) {
    if (revenue.segmentKind !== "revision") continue;
    const amount = revenue.monthlyAmount ?? revenue.amount ?? 0;
    pushMarker(
      `revision-revenue-${revenue.id}`,
      revenue.startDate,
      "revenue",
      revenue.name,
      `${revenue.name} revised → $${Number(amount).toLocaleString()}/mo${revenue.revisionNote ? ` — ${revenue.revisionNote}` : ""}`,
    );
  }

  for (const event of input.events) {
    if (event.type !== "asset" || event.segmentKind !== "revision") continue;
    const rate =
      event.monthlyAppreciationRate != null
        ? `${(event.monthlyAppreciationRate * 100).toFixed(2)}%/mo`
        : "flat";
    pushMarker(
      `revision-asset-${event.id}`,
      event.date,
      "asset",
      event.description || "Asset",
      `${event.description || "Asset"} revised → $${Number(event.amount ?? 0).toLocaleString()} @ ${rate}${event.revisionNote ? ` — ${event.revisionNote}` : ""}`,
    );
  }

  return markers.sort((a, b) => a.progress - b.progress);
}
